'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import Grid from '@enlighten/design-system/dist/Grid';
import Typography from '@enlighten/design-system/dist/Typography';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import type { SubLayoutValue } from '@/constants/navigation';
import { subLayoutValueList } from '@/constants/navigation';

export interface SubLayoutHeaderProps {
	defaultValue?: SubLayoutValue;
}

export default function SubLayoutHeader({ defaultValue }: SubLayoutHeaderProps) {
	const pathname = usePathname();
	const value = defaultValue ?? subLayoutValueList.find((item) => item.pathname === pathname);

	if (value) {
		const { title, subTitle1, mobileSubTitle1, subTitle2, inquire, image, mobileImage } = value;

		return (
			<>
				<Grid maxWidth={1440} margin="0 auto" padding="80px 24px 56px">
					{subTitle1 && !mobileSubTitle1 ? (
						<Typography marginBottom="4px" fontSize={{ xs: 18, md: 22 }} fontWeight={700} lineHeight={{ xs: '28px', md: '32px' }}>
							{subTitle1}
						</Typography>
					) : null}
					{subTitle1 && mobileSubTitle1 ? (
						<>
							<Typography marginBottom="4px" fontSize={22} fontWeight={700} lineHeight="32px" display={{ xs: 'none', md: 'block' }}>
								{subTitle1}
							</Typography>
							<Typography marginBottom="4px" fontSize={18} fontWeight={700} lineHeight="28px" whiteSpace="pre-line" display={{ xs: 'block', md: 'none' }}>
								{mobileSubTitle1}
							</Typography>
						</>
					) : null}
					<Typography fontSize={{ xs: 34, md: 48 }} fontWeight={700} lineHeight={{ xs: '44px', md: '62px' }} whiteSpace={{ xs: 'pre-line', md: 'normal' }}>
						{title}
					</Typography>
					{subTitle2 && (
						<Typography fontSize={{ xs: 18, md: 22 }} fontWeight={{ xs: 400, md: 700 }} lineHeight={1.3}>
							{subTitle2}
						</Typography>
					)}
					{'desktop' in inquire ? (
						<>
							<Box display={{ xs: 'none', md: 'block' }}>
								{Array.isArray(inquire.desktop) ? (
									<Grid container alignItems="center" gap={2} marginTop={2}>
										{inquire.desktop.map((buttonProps, i) =>
											buttonProps && 'href' in buttonProps ? (
												<Button key={i} LinkComponent={Link} role="link" {...buttonProps}>
													{buttonProps.title}
												</Button>
											) : (
												buttonProps
											)
										)}
									</Grid>
								) : 'href' in inquire.desktop ? (
									<Button LinkComponent={Link} role="link" sx={{ marginTop: 2, paddingX: '23px' }} {...inquire.desktop}>
										{inquire.desktop.title}
									</Button>
								) : (
									inquire.desktop
								)}
							</Box>
							<Box display={{ xs: 'block', md: 'none' }}>
								{Array.isArray(inquire.mobile) ? (
									<Grid container alignItems="center" gap={2} marginTop={2}>
										{inquire.mobile.map((buttonProps, i) =>
											buttonProps && 'href' in buttonProps ? (
												<Button key={i} LinkComponent={Link} role="link" {...buttonProps}>
													{buttonProps.title}
												</Button>
											) : (
												buttonProps
											)
										)}
									</Grid>
								) : 'href' in inquire.mobile ? (
									<Button LinkComponent={Link} role="link" sx={{ marginTop: 2, paddingX: '23px' }} {...inquire.mobile}>
										{inquire.mobile.title}
									</Button>
								) : (
									inquire.mobile
								)}
							</Box>
						</>
					) : Array.isArray(inquire) ? (
						<Grid container alignItems="center" gap={2} marginTop={2}>
							{inquire.map((buttonProps, i) =>
								buttonProps && 'href' in buttonProps ? (
									<Button key={i} LinkComponent={Link} role="link" {...buttonProps}>
										{buttonProps.title}
									</Button>
								) : (
									buttonProps
								)
							)}
						</Grid>
					) : 'href' in inquire ? (
						<Button LinkComponent={Link} role="link" sx={{ marginTop: 2, paddingX: '23px' }} {...inquire}>
							{inquire.title}
						</Button>
					) : (
						inquire
					)}
				</Grid>
				<Grid
					height={{ xs: 270, md: 640 }}
					sx={{
						background: {
							xs: `url(${mobileImage ? mobileImage.src : image.src}) no-repeat center / cover`,
							md: `url(${image.src}) no-repeat center / cover`,
						},
					}}
				/>
			</>
		);
	}

	return <></>;
}
